import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { AuthProvider } from "@/hooks/useAuth";
import Layout from "@/components/layout/Layout";
import AdminGuard from "@/components/admin/AdminGuard";
import AdminLayout from "@/components/admin/AdminLayout";
import Index from "./pages/Index";

// Lazy-loaded public pages
const About = lazy(() => import("./pages/About"));
const CV = lazy(() => import("./pages/CV"));
const Lectures = lazy(() => import("./pages/Lectures"));
const Media = lazy(() => import("./pages/Media"));
const Partners = lazy(() => import("./pages/Partners"));
const Certificates = lazy(() => import("./pages/Certificates"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Blog = lazy(() => import("./pages/Blog"));
const GoodDeeds = lazy(() => import("./pages/GoodDeeds"));
const Contact = lazy(() => import("./pages/Contact"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy-loaded admin pages
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminPosts = lazy(() => import("./pages/admin/AdminPosts"));
const AdminLectures = lazy(() => import("./pages/admin/AdminLectures"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminCertificates = lazy(() => import("./pages/admin/AdminCertificates"));
const AdminGallery = lazy(() => import("./pages/admin/AdminGallery"));
const AdminCV = lazy(() => import("./pages/admin/AdminCV"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const ContactSettings = lazy(() => import("./pages/admin/settings/ContactSettings"));
const SocialSettings = lazy(() => import("./pages/admin/settings/SocialSettings"));
const GeneralSettings = lazy(() => import("./pages/admin/settings/GeneralSettings"));
const HomeAboutSettings = lazy(() => import("./pages/admin/settings/HomeAboutSettings"));
const AchievementsSettings = lazy(() => import("./pages/admin/settings/AchievementsSettings"));
const AboutPageSettings = lazy(() => import("./pages/admin/settings/AboutPageSettings"));
const CVSettings = lazy(() => import("./pages/admin/settings/CVSettings"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminGoodDeeds = lazy(() => import("./pages/admin/AdminGoodDeeds"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={null}>
          <Routes>
            {/* Redirect root to default language */}
            <Route path="/" element={<Navigate to="/he" replace />} />

            {/* Redirect unprefixed paths to default language for SEO/sitemap compatibility */}
            <Route path="/about" element={<Navigate to="/he/about" replace />} />
            <Route path="/cv" element={<Navigate to="/he/cv" replace />} />
            <Route path="/expertise" element={<Navigate to="/he" replace />} />
            <Route path="/lectures" element={<Navigate to="/he/lectures" replace />} />
            <Route path="/media" element={<Navigate to="/he/media" replace />} />
            <Route path="/partners" element={<Navigate to="/he/partners" replace />} />
            <Route path="/certificates" element={<Navigate to="/he/certificates" replace />} />
            <Route path="/gallery" element={<Navigate to="/he/gallery" replace />} />
            <Route path="/good-deeds" element={<Navigate to="/he/good-deeds" replace />} />
            <Route path="/blog" element={<Navigate to="/he/blog" replace />} />
            <Route path="/contact" element={<Navigate to="/he/contact" replace />} />
            <Route path="/privacy" element={<Navigate to="/he/privacy" replace />} />
            <Route path="/terms" element={<Navigate to="/he/terms" replace />} />
            <Route path="/accessibility" element={<Navigate to="/he/accessibility" replace />} />

            {/* Language-prefixed routes */}
            <Route path="/:lang" element={<LanguageProvider><Layout /></LanguageProvider>}>
              <Route index element={<Index />} />
              <Route path="about" element={<About />} />
              <Route path="cv" element={<CV />} />
              <Route path="expertise" element={<Navigate to="/" replace />} />
              <Route path="lectures" element={<Lectures />} />
              <Route path="media" element={<Media />} />
              <Route path="partners" element={<Partners />} />
              <Route path="certificates" element={<Certificates />} />
              <Route path="gallery" element={<Gallery />} />
              <Route path="good-deeds" element={<GoodDeeds />} />
              <Route path="blog" element={<Blog />} />
              <Route path="blog/:slug" element={<Blog />} />
              <Route path="contact" element={<Contact />} />
              <Route path="thank-you" element={<ThankYou />} />
              <Route path="privacy" element={<Privacy />} />
              <Route path="terms" element={<Terms />} />
              <Route path="accessibility" element={<Accessibility />} />
            </Route>

            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminGuard><AdminLayout /></AdminGuard>}>
              <Route index element={<Dashboard />} />
              <Route path="posts" element={<AdminPosts />} />
              <Route path="lectures" element={<AdminLectures />} />
              <Route path="media" element={<AdminMedia />} />
              <Route path="partners" element={<AdminPartners />} />
              <Route path="certificates" element={<AdminCertificates />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="cv" element={<AdminCV />} />
              <Route path="good-deeds" element={<AdminGoodDeeds />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="settings/contact" element={<ContactSettings />} />
              <Route path="settings/social" element={<SocialSettings />} />
              <Route path="settings/general" element={<GeneralSettings />} />
              <Route path="settings/home-about" element={<HomeAboutSettings />} />
              <Route path="settings/achievements" element={<AchievementsSettings />} />
              <Route path="settings/about-page" element={<AboutPageSettings />} />
              <Route path="settings/cv" element={<CVSettings />} />
              <Route path="users" element={<AdminUsers />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
